// src/lib/supabase.js — COMPLETE
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// ─── AUTH ──────────────────────────────────────
export async function signUp({ email, password, displayName, tier, birthday, refCode }) {
  const referralCode = Math.random().toString(36).substring(2,10).toUpperCase()
  return supabase.auth.signUp({ email, password, options: { data: { display_name: displayName, tier, birthday: birthday||null, referral_code: referralCode, referred_by: refCode||null } } })
}
export async function signIn({ email, password }) { return supabase.auth.signInWithPassword({ email, password }) }
export async function signOut() { return supabase.auth.signOut() }

// ─── PROFILE ───────────────────────────────────
export async function getProfile(userId) {
  return supabase.from('profiles').select('*').eq('id', userId).single()
}

// ─── MESSAGES ──────────────────────────────────
export const SKY_BRI_USER_ID = process.env.REACT_APP_ADMIN_ID || '75317bea-2c63-4374-a1bd-a1769ca7fd1f'

export async function sendMessage({ senderId, receiverId, content }) {
  return supabase.from('messages').insert({ sender_id: senderId, receiver_id: receiverId, content }).select().single()
}

// ─── GOLD SESSIONS ─────────────────────────────
export async function getGoldSession(userId) {
  return supabase.from('gold_sessions').select('*').eq('user_id', userId).single()
}
export async function startGoldSession(userId) {
  const now = new Date(); const expires = new Date(now.getTime() + 24*60*60*1000)
  const { data: existing } = await getGoldSession(userId)
  if (existing) {
    const monthReset = new Date(existing.month_reset_at)
    let used = existing.month_sessions_used, resetAt = existing.month_reset_at
    if (now >= monthReset) { used = 0; resetAt = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth()+1, 1)).toISOString() }
    if (used >= 4) return { error: { message: 'All 4 sessions used this month.' } }
    return supabase.from('gold_sessions').update({ session_started_at: now.toISOString(), session_expires_at: expires.toISOString(), month_sessions_used: used+1, month_reset_at: resetAt, week_number: used+1, updated_at: now.toISOString() }).eq('user_id', userId).select().single()
  }
  return supabase.from('gold_sessions').insert({ user_id: userId, session_started_at: now.toISOString(), session_expires_at: expires.toISOString(), month_sessions_used: 1, week_number: 1, month_reset_at: new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth()+1, 1)).toISOString() }).select().single()
}

// ─── DINNER ────────────────────────────────────
export async function createDinnerBooking({ userId, date, city, note }) {
  return supabase.from('dinner_bookings').insert({ user_id: userId, preferred_date: date, preferred_city: city, special_note: note, status: 'pending' }).select().single()
}
export async function getUserBookings(userId) {
  return supabase.from('dinner_bookings').select('*').eq('user_id', userId).order('created_at', { ascending: false })
}

// ─── VAULT ─────────────────────────────────────
export async function getVaultContent() { return supabase.from('vault_content').select('*').order('created_at', { ascending: false }) }
export async function addVaultContent({ title, description, mediaUrl, mediaType, minTier }) {
  return supabase.from('vault_content').insert({ title, description: description||null, media_url: mediaUrl, media_type: mediaType, min_tier: minTier }).select().single()
}
export async function deleteVaultContent(id) { return supabase.from('vault_content').delete().eq('id', id) }

// ─── WEEKLY QUESTIONS ──────────────────────────
export async function getActiveQuestion() {
  return supabase.from('weekly_questions').select('*').eq('active', true).order('created_at', { ascending: false }).limit(1).single()
}
export async function createWeeklyQuestion(question) {
  await supabase.from('weekly_questions').update({ active: false }).eq('active', true)
  return supabase.from('weekly_questions').insert({ question, active: true }).select().single()
}
export async function getQuestionResponses(questionId) {
  return supabase.from('question_responses').select('*, profiles(display_name, tier)').eq('question_id', questionId).order('created_at', { ascending: false })
}
export async function replyToResponse(responseId, reply) {
  return supabase.from('question_responses').update({ sky_reply: reply }).eq('id', responseId).select().single()
}

// ─── ANNOUNCEMENTS ─────────────────────────────
export async function getAnnouncements() { return supabase.from('announcements').select('*').eq('active', true).order('event_date', { ascending: true }) }
export async function createAnnouncement({ title, description, eventDate }) {
  return supabase.from('announcements').insert({ title, description: description||null, event_date: eventDate||null, active: true }).select().single()
}
export async function deleteAnnouncement(id) { return supabase.from('announcements').delete().eq('id', id) }

// ─── ACCESS CODES ──────────────────────────────
export async function verifyAccessCode(code) {
  const { data, error } = await supabase.from('access_codes').select('*').eq('code', code.toUpperCase().trim()).eq('used', false).single()
  if (error || !data) return { valid: false, error: 'Invalid or already used code' }
  if (data.expires_at && new Date(data.expires_at) < new Date()) return { valid: false, error: 'This code has expired' }
  return { valid: true, data }
}
export async function claimAccessCode(code, userId) {
  return supabase.from('access_codes').update({ used: true, used_by: userId, used_at: new Date().toISOString() }).eq('code', code.toUpperCase().trim()).eq('used', false)
}
export async function createAccessCode({ tier, paymentMethod, amountPaid, note, expiresAt }) {
  const random = Math.random().toString(36).substring(2,6).toUpperCase()
  const code = `SKY-${tier.substring(0,4).toUpperCase()}-${random}`
  const { data, error } = await supabase.from('access_codes').insert({ code, tier, payment_method: paymentMethod||null, amount_paid: amountPaid||null, note: note||null, expires_at: expiresAt||null }).select().single()
  return { data, error, code }
}
export async function getAccessCodes() { return supabase.from('access_codes').select('*, profiles(display_name, email)').order('created_at', { ascending: false }) }
export async function deleteAccessCode(id) { return supabase.from('access_codes').delete().eq('id', id) }

// ─── PRESENCE ──────────────────────────────────
export async function setSkyBriOnline(isOnline) {
  return supabase.from('presence').upsert({ id: SKY_BRI_USER_ID, is_online: isOnline, last_seen: new Date().toISOString() })
}

// ─── MISSING HELPERS ───────────────────────────
export async function getMessages(userId) {
  return supabase.from('messages').select('*')
    .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`)
    .order('created_at', { ascending: true })
}

export function subscribeToMessages(userId, callback) {
  return supabase.channel('messages-' + userId)
    .on('postgres_changes', { event:'INSERT', schema:'public', table:'messages', filter:`receiver_id=eq.${userId}` }, callback)
    .subscribe()
}

// Tell the server "I've read everything this person sent me" (powers "Seen" and
// the admin inbox's unread counts). Fails quietly if the database update from
// chat-receipts-migration.sql hasn't been run yet.
export async function markConversationRead(otherId) {
  try { await supabase.rpc('mark_conversation_read', { other_id: otherId }) } catch (e) {}
}

// Fires when someone reads a message *I* sent (read_at gets filled in)
export function subscribeToReadReceipts(userId, callback) {
  return supabase.channel('receipts-' + userId)
    .on('postgres_changes', { event:'UPDATE', schema:'public', table:'messages', filter:`sender_id=eq.${userId}` }, callback)
    .subscribe()
}
